import { checkNullString } from '@/pages/datalab/utils/utils';
import type { IColumnInfo } from '@dbl/core/dataSource';
import type { TDispatchFun } from '@/pages/datalab/components/SortableDrag/configration';
import { ChartType } from '@dbl/core/chart';
import type { DimensionMapInfo } from '@/pages/datalab/pages/Multidimensional/Analysis/interface';

/**
 * 展示维度区写回 model 的统一入口。
 * 覆盖拖拽落库、close 删除、批量下拉增删；与 showDimensionSortable 单条路径副作用对齐。
 */

export interface IShowDimensionSortablePersistOptions {
  sortableKey: string;
  chartType: string;
  filters?: IColumnInfo[];
  dimensionMap?: DimensionMapInfo;
  dispatchFun: TDispatchFun;
}

export interface IShowDimensionSortablePersist {
  /** 数字趋势图首项不可为 date/time，失败时返回 false */
  saveList: (list: IColumnInfo[]) => boolean;
  removeItems: (currentList: IColumnInfo[], removedItems: IColumnInfo[]) => void;
  applyBatchChange: (
    nextList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ) => boolean;
  /** 无维度映射配置时提示 errorDrag（不阻塞 save） */
  notifyDimensionMapIfNeeded: (nextList: IColumnInfo[]) => void;
}

const isInvalidShowDimensionFirstField = (
  chartType: string,
  nextList: IColumnInfo[],
): boolean =>
  chartType === ChartType.DimNumberTrend &&
  nextList.length > 0 &&
  ['date', 'time'].includes(nextList[0].column_type || '');

export const createShowDimensionSortablePersist = (
  options: IShowDimensionSortablePersistOptions,
): IShowDimensionSortablePersist => {
  const { sortableKey, chartType, filters, dimensionMap, dispatchFun } = options;

  const normalizeShowDimensionList = (list: IColumnInfo[]): IColumnInfo[] =>
    list.filter(item => !checkNullString(item.id));

  const notifyDimensionMapIfNeeded = (nextList: IColumnInfo[]): void => {
    if (!(dimensionMap?.data?.length ?? 0) && nextList.length > 0) {
      // currentDrag 须为空，否则其它已满区域会因 disabledDrag && currentDrag 误显 disabledError
      dispatchFun('saveDrag', {
        dragState: {
          currentDrag: '',
          errorDrag: {
            [sortableKey]: `${ChartType.DimNumberTrend}_dimensionMap_null`,
          },
        },
      });
    }
  };
  const buildSavePayload = (
    nextList: IColumnInfo[],
    removedItems: IColumnInfo[] = [],
  ): Record<string, unknown> => {
    const payload: Record<string, unknown> = {
      [sortableKey]: nextList,
    };
    if (removedItems.length > 0) {
      payload.customDimensionMap = { options: [], data: [], loading: true };
      const removedIds = new Set(removedItems.map(item => item.id));
      payload.columnFilters = (filters || []).filter(
        item => !checkNullString(item.id) && !removedIds.has(item.id),
      );
    }
    return payload;
  };

  const saveList = (list: IColumnInfo[]): boolean => {
    const nextList = normalizeShowDimensionList(list);
    if (isInvalidShowDimensionFirstField(chartType, nextList)) {
      dispatchFun('saveDrag', {
        dragState: {
          currentDrag: sortableKey,
          errorDrag: { [sortableKey]: `${ChartType.DimNumberTrend}_showDimension` },
        },
      });
      return false;
    }
    notifyDimensionMapIfNeeded(nextList);
    dispatchFun('save', buildSavePayload(nextList));
    return true;
  };

  const applyBatchChange = (
    nextList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): boolean => {
    const normalized = normalizeShowDimensionList(nextList);
    if (isInvalidShowDimensionFirstField(chartType, normalized)) {
      return false;
    }
    notifyDimensionMapIfNeeded(normalized);
    dispatchFun('save', buildSavePayload(normalized, removedItems));
    return true;
  };

  const removeItems = (
    currentList: IColumnInfo[],
    removedItems: IColumnInfo[],
  ): void => {
    if (!removedItems.length) {
      return;
    }
    const removedKeySet = new Set(
      removedItems.map(item => `${item.id}_${item.table_code}`),
    );
    const nextList = currentList.filter(item => {
      if (checkNullString(item.id)) {
        return false;
      }
      return !removedKeySet.has(`${item.id}_${item.table_code}`);
    });
    applyBatchChange(nextList, removedItems);
  };

  return {
    saveList,
    removeItems,
    applyBatchChange,
    notifyDimensionMapIfNeeded,
  };
};
